// Hook for returning values and listeners needed to close a hover popover if the content contains a link.
// This cleans up the popover as it will stick in until your mouse leaves its activation container.
import * as React from "react"
import { type PseudoTargetDirection } from "@digits-shared/DesignSystem/Popovers/HoverPopover"
import useForceUpdate from "@digits-shared/hooks/useForceUpdate"

export const useHoverPopoverCloseOnClick = () => {
  // key will be used to force
  const [hoverPopoverKey, hideHoverPopover] = useForceUpdate()

  const clickListener = React.useCallback(
    (event: React.MouseEvent) => {
      // If the target of the click listener has a link ancestor, we want to close the hover popover
      if (event.target instanceof Element && event.target.closest("a")) {
        hideHoverPopover()
      }

      // Also want to always stop propagation for the hover popover
      event.stopPropagation()
      event.nativeEvent.stopImmediatePropagation()
    },
    [hideHoverPopover]
  )

  const contentRef = React.useRef<HTMLDivElement | null>(null)
  const triggerRef = React.useRef<HTMLDivElement | null>(null)
  const [openedDirection, setOpenedDirection] = React.useState<PseudoTargetDirection>("downward")

  const determineLayout = React.useCallback(() => {
    if (contentRef.current && triggerRef.current) {
      const contentRect = contentRef.current.getBoundingClientRect()
      const triggerRect = triggerRef.current.getBoundingClientRect()

      const direction: PseudoTargetDirection =
        contentRect.top < triggerRect.top ? "upward" : "downward"
      setOpenedDirection(direction)
    }
  }, [contentRef, triggerRef])

  const onHoverPopoverOpenChange = React.useCallback(
    (open: boolean) => {
      if (open) setTimeout(determineLayout, 0)
    },
    [determineLayout]
  )

  return React.useMemo(
    () => ({
      hoverPopoverKey,
      onHoverPopoverOpenChange,
      clickListener,
      hideHoverPopover,
      hoverPopoverContentRef: contentRef,
      hoverPopoverTriggerRef: triggerRef,
      pseudoTargetDirection: openedDirection,
    }),
    [hoverPopoverKey, onHoverPopoverOpenChange, clickListener, hideHoverPopover, openedDirection]
  )
}
